import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import RoomChecklist from "@/components/RoomChecklist";
import ShoppingList from "@/components/ShoppingList";

const COLUMNS = [
  { key: "offen", label: "Offen", color: "bg-slate-100" },
  { key: "in Arbeit", label: "In Arbeit", color: "bg-blue-50" },
  { key: "erledigt", label: "Erledigt", color: "bg-emerald-50" },
];

const PRIO_COLORS = { hoch: "bg-red-100 text-red-800", normal: "bg-slate-100 text-slate-700", niedrig: "bg-blue-100 text-blue-800" };

const TASK_TYPES = [
  "Zimmer reinigen","Handtücher auffüllen","Bettwäsche wechseln",
  "Frühstück vorbereiten","Mittagessen vorbereiten","Abendessen vorbereiten",
  "Check-in vorbereiten","Rechnung prüfen","Sonderwunsch erledigen",
  "Schaden prüfen","Gast abgereist, Zimmer kontrollieren"
];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "Zimmer reinigen", description: "", priority: "normal", status: "offen", due_date: "" });

  const load = () => api.get("/tasks").then(r => setTasks(r.data));
  const loadRooms = () => api.get("/rooms").then(r => setRooms(r.data));
  useEffect(() => { load(); loadRooms(); }, []);

  const create = async () => {
    try { await api.post("/tasks", form); toast.success("Aufgabe erstellt"); setOpen(false); load(); }
    catch { toast.error("Fehler"); }
  };
  const move = async (id, status) => {
    try { await api.patch(`/tasks/${id}`, { status }); load(); }
    catch { toast.error("Fehler"); }
  };
  const removeTask = async (id) => {
    try { await api.delete(`/tasks/${id}`); load(); }
    catch { toast.error("Konnte Aufgabe nicht löschen"); }
  };
  const clearDone = async () => {
    const done = tasks.filter(t => t.status === "erledigt");
    if (!done.length) return;
    if (!window.confirm(`${done.length} erledigte Aufgabe(n) endgültig löschen?`)) return;
    try {
      await Promise.all(done.map(t => api.delete(`/tasks/${t.id}`)));
      toast.success(`${done.length} erledigte Aufgabe(n) gelöscht`);
      load();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Aufgaben</h1>
          <p className="text-muted-foreground mt-1">Kanban-Board für tägliche Abläufe</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-primary" data-testid="new-task-btn"><Plus size={16} weight="bold"/> <span className="ml-1.5">Neue Aufgabe</span></Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Neue Aufgabe</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titel</Label>
                <Select value={form.title} onValueChange={v=>setForm({...form,title:v})}>
                  <SelectTrigger className="rounded-xl mt-1.5"><SelectValue/></SelectTrigger>
                  <SelectContent>{TASK_TYPES.map(t=><SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Beschreibung</Label><Textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="rounded-xl mt-1.5"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Priorität</Label>
                  <Select value={form.priority} onValueChange={v=>setForm({...form,priority:v})}>
                    <SelectTrigger className="rounded-xl mt-1.5"><SelectValue/></SelectTrigger>
                    <SelectContent>{["niedrig","normal","hoch"].map(p=><SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Fällig am</Label><Input type="date" value={form.due_date} onChange={e=>setForm({...form,due_date:e.target.value})} className="rounded-xl mt-1.5"/></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-xl">Abbrechen</Button>
              <Button onClick={create} className="rounded-xl bg-primary" data-testid="save-task-btn">Erstellen</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Einkaufsliste — Autocomplete lernt aus vergangenen Einkäufen */}
      <ShoppingList />

      {/* Reinigungs-Checklisten je Zimmer — für die Mitarbeiterin */}
      {rooms.length > 0 && (
        <section data-testid="room-checklists-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="text-xl font-semibold" style={{fontFamily:"'Work Sans',sans-serif"}}>
              Zimmer-Checklisten
            </h2>
            <span className="text-xs text-muted-foreground">
              Haken setzen · alle Häkchen = Zimmer wird automatisch „Frei"
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rooms.map(r => (
              <RoomChecklist key={r.id} room={r} onRoomChange={loadRooms}/>
            ))}
          </div>
        </section>
      )}

      <div className="pt-2">
        <h2 className="text-xl font-semibold mb-3" style={{fontFamily:"'Work Sans',sans-serif"}}>
          Aufgaben-Board
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {COLUMNS.map(col => (
          <div key={col.key} className="space-y-3" data-testid={`task-col-${col.key}`}>
            <div className={`rounded-2xl px-4 py-2 ${col.color} font-semibold text-sm flex justify-between items-center`} style={{fontFamily:"'Work Sans',sans-serif"}}>
              <span>{col.label}</span>
              <div className="flex items-center gap-2">
                {col.key === "erledigt" && tasks.filter(t=>t.status==="erledigt").length > 0 && (
                  <button onClick={clearDone} data-testid="clear-done-btn"
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                    title="Alle erledigten Aufgaben löschen">
                    Alle löschen
                  </button>
                )}
                <span className="text-muted-foreground">{tasks.filter(t=>t.status===col.key).length}</span>
              </div>
            </div>
            {tasks.filter(t => t.status === col.key).map(t => {
              const persons = t.guests_count;
              return (
              <div key={t.id} className="msb-card p-4 relative group" data-testid={`task-card-${t.id}`}>
                <button
                  onClick={() => removeTask(t.id)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600 hover:bg-red-50 rounded p-0.5"
                  title="Aufgabe löschen"
                  data-testid={`del-task-${t.id}`}
                >✕</button>
                <div className="flex items-start justify-between gap-2 pr-6">
                  <div className="font-medium text-sm">{t.title}</div>
                  <span className={`status-pill ${PRIO_COLORS[t.priority]}`}>{t.priority}</span>
                </div>
                {(t.room_name || persons != null) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {t.room_name && (
                      <span className="inline-flex items-center gap-1 rounded-lg bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-semibold uppercase tracking-widest"
                            data-testid={`task-room-${t.id}`}>
                        {t.room_name}
                      </span>
                    )}
                    {persons != null && (
                      <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[11px] font-semibold ${persons >= 2 ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}
                            title={persons >= 2 ? "Doppelbelegung — Zimmer für 2 Personen vorbereiten" : "Einzelbelegung — Zimmer für 1 Person vorbereiten"}
                            data-testid={`task-persons-${t.id}`}>
                        {persons === 1 ? "1 Person" : `${persons} Personen`}
                      </span>
                    )}
                    {t.guest_name && (
                      <span className="text-[11px] text-muted-foreground">{t.guest_name}</span>
                    )}
                  </div>
                )}
                {t.due_date && <div className="text-xs text-muted-foreground mt-2">Fällig: {formatDate(t.due_date)}</div>}
                {(t.check_in && t.check_out) && (
                  <div className="text-xs text-muted-foreground">
                    {formatDate(t.check_in)} → {formatDate(t.check_out)}{t.nights ? ` · ${t.nights} Nächte` : ""}
                  </div>
                )}
                {t.description && <div className="text-xs mt-2 whitespace-pre-line">{t.description}</div>}
                <div className="mt-3 flex gap-1">
                  {COLUMNS.filter(c => c.key !== t.status).map(c => (
                    <Button key={c.key} size="sm" variant="outline" className="rounded-lg text-xs h-7" onClick={()=>move(t.id, c.key)} data-testid={`move-${t.id}-${c.key}`}>
                      → {c.label}
                    </Button>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
